import React, { useContext, useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import CustomModal from "../../../../../ui/CustomModal";
import AuthContext from "../../../../../contexts/AuthContext";
import { CircularProgress } from "@mui/material";
import CustomTag from "../../../../../ui/CustomTag";
import { toast } from "react-toastify";
import { toastOptions } from "../../../../../assets/constants";
import {PlusIcon, TrashIcon, EyeIcon} from "@phosphor-icons/react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

const SortableContractRow = ({ contract, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: contract.id,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <tr ref={setNodeRef} style={style} {...attributes}>
            <td className="entry-custom-order" {...listeners} style={{ display: "table-cell" }}>
                <DragIndicatorIcon fontSize="small" style={{ color: "#999" }} />
            </td>
            {children}
        </tr>
    );
};

const CustomerContracts = ({ contact, onContractsChange }) => {
    const [contracts, setContracts] = useState([]);
    const [isLoaded, setIsLoaded] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [refreshData, setRefreshData] = useState(false);
    const { token } = useContext(AuthContext);

    const sensors = useSensors(useSensor(PointerSensor));

    // État pour le formulaire
    const [formData, setFormData] = useState({
        contractName: '',
        file: null
    });

    const formatDate = useCallback((dateString) => {
        if (!dateString) return "Non renseigné";

        const options = {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        };

        return new Date(dateString).toLocaleDateString("fr-FR", options);
    }, []);

    const loadContracts = useCallback(async () => {
        if (!contact?.reference) return;

        setIsLoaded(false);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_UPA_API_HOST}contracts/${contact.reference}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            // Les contrats arrivent déjà triés par sortOrder du backend
            setContracts(response.data.data || []);
        } catch (error) {
            console.error("Erreur lors de la récupération des contrats:", error);
            toast.error("Erreur lors de la récupération des contrats", toastOptions);
        } finally {
            setIsLoaded(true);
        }
    }, [contact?.reference, token]);

    useEffect(() => {
        const initializeContracts = async () => {
            if (contact?.reference) {
                await loadContracts();
            }
        };

        initializeContracts();
    }, [contact?.reference, refreshData, loadContracts]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type === 'application/pdf') {
            setFormData(prev => ({
                ...prev,
                file: file
            }));
        } else {
            toast.error("Veuillez sélectionner un fichier PDF", toastOptions);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.contractName.trim()) {
            toast.error("Veuillez saisir un nom de contrat", toastOptions);
            return;
        }

        if (!formData.file) {
            toast.error("Veuillez sélectionner un fichier PDF", toastOptions);
            return;
        }

        setIsUploading(true);

        const uploadFormData = new FormData();
        uploadFormData.append('contractFile', formData.file);
        uploadFormData.append('contractName', formData.contractName);

        try {
            await axios.post(
                `${process.env.REACT_APP_UPA_API_HOST}contracts/${contact.reference}/add`,
                uploadFormData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            toast.success("Contrat ajouté avec succès", toastOptions);
            setShowModal(false);
            setFormData({ contractName: '', file: null });
            setRefreshData(prev => !prev);
            if (onContractsChange) onContractsChange();

        } catch (error) {
            console.error("Erreur lors de l'ajout du contrat:", error);
            toast.error(
                error.response?.data?.message || "Erreur lors de l'ajout du contrat",
                toastOptions
            );
        } finally {
            setIsUploading(false);
        }
    };

    const handleDeleteContract = async (contractId) => {
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce contrat ?")) {
            return;
        }

        try {
            await axios.delete(
                `${process.env.REACT_APP_UPA_API_HOST}contracts/${contact.reference}/delete`,
                {
                    data: { contractId },
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            toast.success("Contrat supprimé avec succès", toastOptions);
            setRefreshData(prev => !prev);
            if (onContractsChange) onContractsChange();

        } catch (error) {
            console.error("Erreur lors de la suppression:", error);
            toast.error(
                error.response?.data?.message || "Erreur lors de la suppression du contrat",
                toastOptions
            );
        }
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;

        if (!over || active.id === over.id) return;

        const activeIndex = contracts.findIndex(contract => contract.id === active.id);
        const overIndex = contracts.findIndex(contract => contract.id === over.id);

        if (activeIndex !== -1 && overIndex !== -1) {
            const reorderedContracts = [...contracts];
            const [movedContract] = reorderedContracts.splice(activeIndex, 1);
            reorderedContracts.splice(overIndex, 0, movedContract);

            const updatedContracts = reorderedContracts.map((contract, index) => ({
                ...contract,
                sortOrder: index
            }));

            setContracts(updatedContracts);

            const contractsOrder = updatedContracts.map(contract => ({
                id: contract.id,
                sortOrder: contract.sortOrder
            }));

            try {
                await axios.post(
                    `${process.env.REACT_APP_UPA_API_HOST}contracts/${contact.reference}/reorder`,
                    { contractsOrder },
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );
                toast.success("Ordre des contrats sauvegardé", toastOptions);
            } catch (error) {
                console.error("Erreur lors de la sauvegarde:", error);
                toast.error("Erreur lors de la sauvegarde de l'ordre", toastOptions);
                setRefreshData(prev => !prev);
            }
        }
    };


    if (!isLoaded) {
        return (
            <div className="loader-container absolute">
                <CircularProgress size={30} />
            </div>
        );
    }

    return (
        <>
            {contracts.length > 0 ? (
                <div className="custom-tracking-table-container">
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={contracts.map(contract => contract.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            <table className="custom-tracking-table">
                                <thead>
                                <tr>
                                    <th className="customOrder"></th>
                                    <th className="contract-name">Nom du contrat</th>
                                    <th className="date">Date de téléchargement</th>
                                    <th className="actions">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {contracts.map((contract) => (
                                    <SortableContractRow key={contract.id} contract={contract}>
                                        <td className="entry-line-contract">
                                            <span>{contract.contractName}</span>
                                        </td>
                                        <td className="entry-line-date">
                                            <CustomTag className="default" value={formatDate(contract.createdAt)} />
                                        </td>
                                        <td className="entry-line-actions">
                                            <div className="action-buttons-container">
                                                <button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        const pdfUrl = `https://image.store.altelis.com/files/${contract.path}`;
                                                        window.open(pdfUrl, '_blank');
                                                    }}
                                                    className="action-button view-button"
                                                    title="Visualiser"
                                                >
                                                    <EyeIcon size={16} weight="bold" />
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleDeleteContract(contract.id);
                                                    }}
                                                    className="action-button delete-button"
                                                    title="Supprimer"
                                                >
                                                    <TrashIcon size={16} weight="bold" />
                                                </button>
                                            </div>
                                        </td>
                                    </SortableContractRow>
                                ))}
                                </tbody>
                            </table>
                        </SortableContext>
                    </DndContext>
                </div>
            ) : (
                <div style={{ textAlign: 'center'}}>
                    <CustomTag
                        value="Aucun contrat n'a été ajouté pour ce client."
                        className="blue"
                    />
                </div>
            )}

            <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <button
                    onClick={() => setShowModal(true)}
                    className="button-large blue-button"
                >
                    <PlusIcon size={16} />
                    <span>Ajouter un contrat</span>
                </button>
            </div>

            <CustomModal
                title="Ajouter un nouveau contrat"
                show={showModal}
                setShow={setShowModal}
            >
                <div className="c-modal-content">
                    <form id="contract-form" onSubmit={handleSubmit}>
                        <div className="custom__modal-group" style={{ marginBottom: '20px' }}>
                            <label className="tag-simple">
                                Nom du contrat <span className="required-asterisk">*</span>
                            </label>
                            <input
                                type="text"
                                name="contractName"
                                value={formData.contractName}
                                onChange={handleInputChange}
                                className="classic-input"
                                placeholder="Ex: Contrat de prestation 2025"
                                required
                            />
                        </div>

                        <div className="custom__modal-group">
                            <label className="url">
                                Fichier PDF <span className="required-asterisk">*</span>
                            </label>
                            <input
                                type="file"
                                id="contractFile"
                                name="contractFile"
                                onChange={handleFileChange}
                                accept=".pdf,application/pdf"
                                style={{ display: 'none' }}
                                required
                            />

                            <label
                                htmlFor="contractFile"
                                className="button-large outline without-icon"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    cursor: 'pointer',
                                    marginTop: '5px'
                                }}
                            >
                                <PlusIcon size={16} />
                                <span>
                                    {formData.file ? 'Changer le fichier' : 'Choisir un fichier PDF'}
                                </span>
                            </label>

                            {formData.file && (
                                <div style={{
                                    marginTop: '10px',
                                    padding: '10px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '4px',
                                    border: '1px solid #e9ecef'
                                }}>
                                    <span style={{
                                        fontSize: '14px',
                                        color: '#28a745',
                                        fontWeight: '500'
                                    }}>
                                        📄 {formData.file.name}
                                    </span>
                                </div>
                            )}
                        </div>
                    </form>
                    <div className="modal-buttons">
                        <button
                            type="button"
                            onClick={() => setShowModal(false)}
                            className="button-large cancel-button"
                            disabled={isUploading}
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            form="contract-form"
                            className="button-large green-button validate"
                            disabled={isUploading}
                        >
                            {isUploading ? (
                                <CircularProgress size={16} className="modal-progress" />
                            ) : (
                                <PlusIcon size={16} />
                            )}
                            <span>
                                    {isUploading ? 'Upload en cours...' : 'Ajouter le contrat'}
                                </span>
                        </button>
                    </div>
                </div>
            </CustomModal>
        </>
    );
};

export default CustomerContracts;